import * as React from "react"
import { useTranslation } from "react-i18next"
import { MediaPreview, cn } from "@nexus/ui"
import type { MediaSlide } from "@nexus/ui"

import { humanizePersonaCompetitor } from "../../lib/persona-format"
import { resolvePersonaAdMedia } from "../../lib/persona-ad-media"
import type { PersonaTopAd } from "../../types/personas.types"

/** Rail shows at most the top 3 ads by spend (Figma Make `assetCardsFor`). */
const MAX_RAIL_ADS = 3
/** Figma Make: 250px rail, 180px cards, ± step 158px, side scale 0.8 / opacity 0.45. */
const RAIL_WIDTH_PX = 250
const AD_CARD_WIDTH_PX = 180
const AD_CARD_OFFSET_PX = 158
const AD_SIDE_SCALE = 0.8
const AD_SIDE_OPACITY = 0.45
const EMPTY_CARD_WIDTH_PX = 133
const AD_CARD_EASE = "cubic-bezier(0.32, 0.72, 0, 1)"

/**
 * Right rail — Figma Make horizontal ad carousel (`assetCardsFor`):
 * neighbors feature on click; center opens MediaPreview lightbox; video autoplays when focused.
 */
export function AdExamplesRail({
  isFocused,
  title,
  topAds,
}: {
  isFocused: boolean
  title: string
  topAds: PersonaTopAd[] | null
}) {
  const { t } = useTranslation()
  const ads = (topAds ?? []).slice(0, MAX_RAIL_ADS)
  const adKey = ads.map((ad) => ad.hash).join("|")
  const [featuredIndex, setFeaturedIndex] = React.useState(0)
  const [isMuted, setIsMuted] = React.useState(true)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  const safeFeatured = ads.length === 0 ? 0 : Math.min(featuredIndex, ads.length - 1)
  const featured = ads[safeFeatured]
  const featuredMedia = featured ? resolvePersonaAdMedia(featured) : null
  const shouldPlayVideo =
    isFocused && featuredMedia?.type === "video" && Boolean(featuredMedia.assetUrl)

  // Featured-first so the lightbox opens on the currently centered rail card.
  const slides: MediaSlide[] = React.useMemo(() => {
    const ordered = [...ads.slice(safeFeatured), ...ads.slice(0, safeFeatured)]
    return ordered.map((ad) => {
      const media = resolvePersonaAdMedia(ad)
      return { assetUrl: media.assetUrl, thumbnailUrl: media.thumbnailUrl, type: media.type }
    })
  }, [ads, safeFeatured])

  React.useEffect(() => {
    setFeaturedIndex(0)
  }, [adKey])

  React.useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.muted = isMuted
    if (shouldPlayVideo) {
      const playResult = el.play()
      if (playResult && typeof playResult.catch === "function") {
        void playResult.catch(() => {})
      }
    } else {
      el.pause()
    }
  }, [shouldPlayVideo, isMuted, featuredMedia?.assetUrl])

  if (ads.length === 0) {
    return (
      <div
        className="relative hidden shrink-0 overflow-hidden rounded-[10px] sm:block"
        style={{ width: RAIL_WIDTH_PX }}
      >
        <span className="absolute top-2 left-1/2 z-30 -translate-x-1/2 text-[9px] font-semibold tracking-[0.06em] text-white/70">
          {t("personas.onboarding.review.adExamplesLabel", "AD EXAMPLES")}
        </span>
        <div className="absolute inset-0 overflow-hidden rounded-[10px]">
          <div
            className="absolute top-0 left-1/2 h-full -translate-x-1/2 overflow-hidden rounded-[10px] border border-border bg-[radial-gradient(120%_120%_at_50%_30%,#33323a_0%,#16161b_76%)]"
            style={{ width: EMPTY_CARD_WIDTH_PX }}
          >
            <div
              className="absolute inset-0 opacity-50"
              style={{
                background:
                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 8px, transparent 8px 16px)",
              }}
              aria-hidden
            />
            <div className="absolute inset-0 flex items-center justify-center px-3.5 text-center">
              <span className="font-mono text-[10px] leading-relaxed text-white/70">
                {t(
                  "personas.onboarding.review.adExamplesEmpty",
                  "Examples unlock after creative mapping",
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="relative hidden shrink-0 overflow-hidden rounded-[10px] sm:block"
      style={{ width: RAIL_WIDTH_PX }}
    >
      <span className="absolute top-2 left-1/2 z-30 -translate-x-1/2 text-[9px] font-semibold tracking-[0.06em] text-white/[0.72]">
        {t("personas.onboarding.review.adExamplesLabelWithCount", {
          defaultValue: "AD EXAMPLES · {{count}}",
          count: ads.length,
        })}
      </span>
      <div className="absolute inset-0 overflow-hidden rounded-[10px]">
        {ads.map((ad, index) => {
          const offset = index - safeFeatured
          const isCenter = offset === 0
          const isNeighbor = Math.abs(offset) === 1
          const media = resolvePersonaAdMedia(ad)
          const showVideo = isCenter && shouldPlayVideo
          const daysLive = Math.round(ad.totalDays)
          const daysLiveLabel = daysLive.toLocaleString()
          const competitorLabel = ad.competitor
            ? humanizePersonaCompetitor(ad.competitor)
            : null

          return (
            <div
              key={ad.hash}
              className={cn(
                "absolute top-0 left-1/2 h-full overflow-hidden rounded-[10px] bg-[radial-gradient(120%_120%_at_50%_30%,#33323a_0%,#16161b_76%)]",
                isCenter ? "border border-border" : "border border-transparent",
                !isCenter && isFocused && "cursor-pointer",
              )}
              style={{
                width: AD_CARD_WIDTH_PX,
                transform: `translateX(calc(-50% + ${offset * AD_CARD_OFFSET_PX}px)) scale(${isCenter ? 1 : AD_SIDE_SCALE})`,
                opacity: isCenter ? 1 : isNeighbor ? AD_SIDE_OPACITY : 0,
                zIndex: 10 - Math.abs(offset),
                transition: `transform 0.4s ${AD_CARD_EASE}, opacity 0.4s`,
              }}
              {...(!isCenter
                ? {
                    role: "button" as const,
                    tabIndex: isFocused ? 0 : -1,
                    "aria-disabled": !isFocused,
                    "aria-label": competitorLabel
                      ? t("personas.onboarding.review.adSelectAriaWithCompetitor", {
                          defaultValue: "Show {{competitor}} {{days}}d live ad example",
                          competitor: competitorLabel,
                          days: daysLiveLabel,
                        })
                      : t("personas.onboarding.review.adSelectAria", {
                          defaultValue: "Show {{days}}d live ad example",
                          days: daysLiveLabel,
                        }),
                    onClick: () => {
                      if (!isFocused) return
                      setFeaturedIndex(index)
                    },
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (!isFocused) return
                      if (event.key !== "Enter" && event.key !== " ") return
                      event.preventDefault()
                      setFeaturedIndex(index)
                    },
                  }
                : {})}
            >
              {showVideo ? (
                <video
                  ref={videoRef}
                  key={media.assetUrl}
                  src={media.assetUrl}
                  loop
                  playsInline
                  muted={isMuted}
                  className="absolute inset-0 size-full object-cover"
                />
              ) : (
                <>
                  <img
                    src={media.thumbnailUrl}
                    alt=""
                    className="absolute inset-0 size-full object-cover"
                  />
                  {!isCenter ? (
                    <div
                      className="absolute inset-0 opacity-55"
                      style={{
                        background:
                          "repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 8px, transparent 8px 16px)",
                      }}
                      aria-hidden
                    />
                  ) : null}
                </>
              )}

              {isCenter && featuredMedia ? (
                <MediaPreview
                  src={featuredMedia.assetUrl}
                  thumbnailSrc={featuredMedia.thumbnailUrl}
                  type={featuredMedia.type}
                  alt={title}
                  title={title}
                  slides={slides.length > 1 ? slides : undefined}
                  className="absolute inset-0 z-[5] size-full cursor-pointer bg-transparent [&_img]:opacity-0 [&_video]:opacity-0"
                />
              ) : null}

              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[6] h-10 bg-gradient-to-t from-black/65 to-transparent" />
              <span className="pointer-events-none absolute bottom-[7px] left-[9px] z-[6] max-w-[calc(100%-18px)] truncate text-[10px] font-medium tracking-[0.01em] text-white">
                {competitorLabel
                  ? t("personas.onboarding.review.adLiveDaysWithCompetitor", {
                      defaultValue: "{{competitor}} · {{days}}d live",
                      competitor: competitorLabel,
                      days: daysLiveLabel,
                    })
                  : t("personas.onboarding.review.adLiveDays", {
                      defaultValue: "{{days}}d live",
                      days: daysLiveLabel,
                    })}
              </span>
            </div>
          )
        })}

        {shouldPlayVideo ? (
          <button
            type="button"
            aria-label={
              isMuted
                ? t("personas.onboarding.review.adUnmuteAria", "Unmute ad example")
                : t("personas.onboarding.review.adMuteAria", "Mute ad example")
            }
            className="absolute top-[7px] z-20 flex size-[26px] items-center justify-center rounded-full bg-black/55 text-white shadow-sm"
            style={{ right: `calc(50% - ${AD_CARD_WIDTH_PX / 2}px + 7px)` }}
            onClick={() => setIsMuted((prev) => !prev)}
          >
            {isMuted ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M11 5 6 9H2v6h4l5 4zM22 9l-6 6M16 9l6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M11 5 6 9H2v6h4l5 4z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        ) : null}
      </div>
    </div>
  )
}
